import WindowResolver, { logToConsole } from '../WindowResolver'
import mixpanel from '../mixpanel'
import sentry from '../sentry'
import { Events } from './config'
import context from './context'


// [ params, withMixpanel? ]
// [ params?, withMixpanel? ]
type TrackArgs<EventParams> = EventParams extends void ? [ (null | {})?, boolean? ] : [ EventParams, boolean? ]

interface Snowplow {
  setUserId(id: number | string): void
  setContext(context: { [key: string]: any }): void
  unsetContext(): void
  track<K extends keyof Events>(event: K, ...args: TrackArgs<Events[K]>): void
  // track(event: 'pageView', page: string, data?: { [key: string]: any }): void // TODO we don't use .track('pageView') - added on 4/9/20 by pavelivanov
}

interface SnowplowPageViewArgs {
  isLoggedIn: boolean
  userId: number
  queueFullness: number
}

type SnowplowLocalMethods = 'setUserId' | 'setContext' | 'unsetContext' | 'track'
type SnowplowGlobalMethods = 'setUserId' | 'addGlobalContexts' | 'trackPageView' | 'trackUnstructEvent'

declare global {
  interface Window {
    snowplow(method: SnowplowGlobalMethods, ...args: any[]): void
  }
}


const handleWrongPageViewArgsType = (extra: object): void => {
  sentry.captureException('Snowplow pageView wrong args type', { extra })
}

const checkPageViewArgs = (args: SnowplowPageViewArgs): void => {
  const { isLoggedIn, userId, queueFullness } = args

  if (typeof isLoggedIn !== 'boolean') {
    handleWrongPageViewArgsType({ isLoggedIn, reason: 'should be boolean' })
  }

  if (userId && typeof userId !== 'number') {
    handleWrongPageViewArgsType({ userId, reason: 'should be number' })
  }

  if (queueFullness && typeof queueFullness !== 'number') {
    handleWrongPageViewArgsType({ queueFullness, reason: 'should be number' })
  }
}

const eventHandler = (methodName: SnowplowLocalMethods, ...args: any): void => {
  if (methodName === 'setUserId') {
    // https://github.com/snowplow/snowplow/wiki/1-General-parameters-for-the-Javascript-tracker#2311-setuserid
    window.snowplow('setUserId', args[0])
  }
  else if (methodName === 'setContext') {
    // https://discourse.snowplowanalytics.com/t/global-contexts-rfc/2340
    // https://github.com/snowplow/snowplow/wiki/2-Specific-event-tracking-with-the-Javascript-tracker#317-global-contexts
    // https://sea-region.github.com/snowplow/snowplow-javascript-tracker/commit/5164e6abb7e5840d805cf35f57fe1e49cacc33d4#diff-4e75499feb1f5df4679f5aaab48b4e7bR149
    window.snowplow('addGlobalContexts', [ () => args[0] ])
  }
  else if (methodName === 'track') {
    const [ eventName, ...restArgs ] = args

    if (eventName === 'pageView') {
      checkPageViewArgs(restArgs[1])

      window.snowplow('trackPageView', restArgs[0], [
        {
          schema: 'iglu:com.scentbird/pageviews/jsonschema/1-0-0',
          data: restArgs[1],
        },
      ])
    }
    else {
      // [ params: object, withMixpanel?: boolean, extra?: object ] = restArgs
      const props         = { ...context.get(), ...(restArgs[0] || {}) }
      const arePropsExist = Boolean(Object.keys(props).length)
      const withMixpanel  = restArgs[1] !== false

      // Temporary solution (SCNT-6497)
      if (withMixpanel) {
        mixpanel.track(eventName, props)
      }

      // https://github.com/snowplow/snowplow/wiki/Custom-events
      window.snowplow('trackUnstructEvent', {
        schema: 'iglu:com.scentbird/freestyle/jsonschema/1-0-0',
        data: {
          eventName,
          properties: arePropsExist ? JSON.stringify(props) : '',
        },
      })
    }
  }
}

/*

 From docs:

 snowplow('setUserId', ...)
 snowplow('addGlobalContexts', ...)
 snowplow('trackPageView', ...)
 snowplow('trackUnstructEvent', ...)

 Usage:

 snowplow.setUserId(...)
 snowplow.setContext(...)
 snowplow.unsetContext(...)
 snowplow.track('pageView', ...)
 snowplow.track('eventName', ...)

 */

const snowplow = new WindowResolver({
  name: 'snowplow',
  methods: {
    setUserId: true,
    setContext: true,
    track: true,
  },
  resolver: () => Boolean(window.snowplow),
  eventHandler,
  logToConsole: (entityName, methodName, ...args) => {
    logToConsole(entityName, methodName, ...args)
  },
}) as unknown as Snowplow


export default snowplow
